import React, { useState, useCallback } from 'react';
import styles from './A_CardInput.module.css';
import { useInputMask } from '../../../hooks/useInputMask';
import { getCardFieldError } from '../../../utils/validation';
import cn from 'classnames';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';

interface CardInputProps {
  onCardDataChange?: (data: {
    number: string;
    holder: string;
    expiry: string;
    cvv: string;
  }) => void;
  initialValues?: {
    number?: string;
    holder?: string;
    expiry?: string;
    cvv?: string;
  };
}

export default function A_CardInput({
  onCardDataChange,
  initialValues,
}: CardInputProps) {
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(language, key);
  const [cardData, setCardData] = useState({
    number: initialValues?.number || '',
    holder: initialValues?.holder || '',
    expiry: initialValues?.expiry || '',
    cvv: initialValues?.cvv || '',
  });

  const [errors, setErrors] = useState({
    number: '',
    holder: '',
    expiry: '',
    cvv: '',
  });

  const { formatValue, validateInput } = useInputMask({ type: 'cardNumber' });
  const { formatValue: formatExpiry } = useInputMask({ type: 'expiry' });
  const { formatValue: formatCVV } = useInputMask({ type: 'cvv' });

  const handleInputChange = useCallback(
    (field: keyof typeof cardData, value: string) => {
      let formattedValue = value;
      let newError = '';

      switch (field) {
        case 'number':
          formattedValue = formatValue(value);
          newError = getCardFieldError('number', formattedValue);
          break;

        case 'holder':
          formattedValue = value.toUpperCase();
          newError = getCardFieldError('holder', formattedValue);
          break;

        case 'expiry':
          formattedValue = formatExpiry(value);
          newError = getCardFieldError('expiry', formattedValue);
          break;

        case 'cvv':
          formattedValue = formatCVV(value);
          newError = getCardFieldError('cvv', formattedValue);
          break;
      }

      const newCardData = { ...cardData, [field]: formattedValue };
      const newErrors = { ...errors, [field]: newError };

      setCardData(newCardData);
      setErrors(newErrors);

      onCardDataChange?.(newCardData);
    },
    [cardData, errors, onCardDataChange, formatValue, formatExpiry, formatCVV]
  );

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    field: string
  ) => {
    if (!validateInput(e.key, field)) {
      e.preventDefault();
    }
  };

  return (
    <div className={styles.wrapper}>
      <input
        type='text'
        placeholder={t('payment.cardInput.numberPlaceholder')}
        value={cardData.number}
        onChange={e => handleInputChange('number', e.target.value)}
        onKeyDown={e => handleKeyPress(e, 'cardNumber')}
        maxLength={19}
        className={cn(styles.number, 'text-p-7')}
      />
      <input
        type='text'
        placeholder={t('payment.cardInput.namePlaceholder')}
        value={cardData.holder}
        onChange={e => handleInputChange('holder', e.target.value)}
        className={cn(styles.holder, 'text-p-7')}
      />

      <div className={styles.expiry_cvv}>
        <input
          type='text'
          placeholder={t('payment.cardInput.expiryPlaceholder')}
          value={cardData.expiry}
          onChange={e => handleInputChange('expiry', e.target.value)}
          onKeyDown={e => handleKeyPress(e, 'expiry')}
          maxLength={5}
          className={cn(styles.expiry, 'text-p-7')}
        />
        <input
          type='text'
          placeholder={t('payment.cardInput.cvvPlaceholder')}
          value={cardData.cvv}
          onChange={e => handleInputChange('cvv', e.target.value)}
          onKeyDown={e => handleKeyPress(e, 'cvv')}
          maxLength={4}
          className={cn(styles.cvv, 'text-p-7')}
        />
      </div>
    </div>
  );
}
